import { isNull } from 'es-toolkit';
import { useMemo } from 'react';

import { MarketStats } from '@flipster/apis/v2/market/types';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { formatDatetime } from '@flipster/utils/datetime';
import Num from '@flipster/utils/num';

import { StatDefinitionGrid } from '../stat-definition-grid';
import { volumeMarketCapPercent } from './utils';

const preprocessMarketStats = (stats: MarketStats, currency: string) => {
  const volumeMarketCap = volumeMarketCapPercent(stats.volume24h, stats.marketCap);

  return [
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_Rank' />,
      value: `No. ${stats.rank}`,
      description: <FormattedMessage id='Autogen_common.r251126.s_Rank_Description' />,
    },
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_Market_cap' />,
      value: <Formatted.ShortNumber value={stats.marketCap} prefix='$' precision={2} />,
      description: <FormattedMessage id='Autogen_common.r251126.s_Market_Cap_Description' />,
    },
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_Fully_diluted_market_cap' />,
      value: <Formatted.ShortNumber value={stats.fullyDilutedMarketCap} prefix='$' precision={2} />,
      description: <FormattedMessage id='Autogen_common.r251126.s_Fully_Diluted_Market_Cap_Description' />,
    },
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_Market_dominance' />,
      value: (
        <Formatted.Percent value={Num(stats.marketDominance).multipliedBy(100).toString()} precision={2} toFixed />
      ),
      description: <FormattedMessage id='Autogen_common.r251126.s_Market_Dominance_Description' />,
    },
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_Volume' />,
      value: <Formatted.ShortNumber value={stats.volume24h} prefix='$' precision={2} />,
      description: <FormattedMessage id='Autogen_common.r251126.s_Volume_Description' />,
    },
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_Volume_Market_cap' />,
      value: isNull(volumeMarketCap) ? '-' : <Formatted.Percent value={volumeMarketCap} precision={2} toFixed />,
      description: <FormattedMessage id='Autogen_common.r251126.s_Volume_Market_Cap_Description' />,
    },
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_Circulating_supply' />,
      value: <Formatted.ShortNumber value={stats.circulatingSupply} precision={2} suffix={currency} />,
      description: <FormattedMessage id='Autogen_common.r251126.s_Circulating_Supply_Description' />,
    },
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_Maximum_supply' />,
      value: isNull(stats.maximumSupply) ? (
        '∞'
      ) : (
        <Formatted.ShortNumber value={stats.maximumSupply} precision={2} suffix={currency} />
      ),
      description: <FormattedMessage id='Autogen_common.r251126.s_Maximum_Supply_Description' />,
    },
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_Total_supply' />,
      value: <Formatted.ShortNumber value={stats.totalSupply} precision={2} suffix={currency} />,
      description: <FormattedMessage id='Autogen_common.r251126.s_Total_Supply_Description' />,
    },
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_All_time_high' />,
      value: <Formatted.Number value={stats.allTimeHigh} prefix='$' />,
      sub: formatDatetime(stats.allTimeHighDate, { format: 'yyyy-MM-dd', isUTC: true }),
      description: <FormattedMessage id='Autogen_common.r251126.s_All_Time_High_Description' />,
    },
    {
      label: <FormattedMessage id='Autogen_common.r251126.s_All_time_low' />,
      value: <Formatted.Number value={stats.allTimeLow} prefix='$' />,
      sub: formatDatetime(stats.allTimeLowDate, { format: 'yyyy-MM-dd', isUTC: true }),
      description: <FormattedMessage id='Autogen_common.r251126.s_All_Time_Low_Description' />,
    },
  ];
};

const TokenStats = ({ stats, currency }: { stats: MarketStats; currency: string }) => {
  const marketStats = useMemo(() => preprocessMarketStats(stats, currency), [stats, currency]);

  return (
    <div>
      <span className={css({ display: 'block', textStyle: 'Title/XS', mb: 8 })}>
        <FormattedMessage id='Autogen_common.r251126.s_Token_info' />
      </span>
      <StatDefinitionGrid items={marketStats} className={css({ mb: 16 })} />
      <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Data_is_provided_by_CMC_and_is_for_reference_only_It_is_presented_on_this_basis_and_does_not_serve_as_any_form_of_representation_or_guarantee' />
      </p>
    </div>
  );
};

export default TokenStats;
